import type { RouteRecordRaw } from 'vue-router'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import { ROUTES } from '~/scopes/auth/route-names'

import { CHECKOUT_GIFT_CARDS } from './route-names'

export default (): RouteRecordRaw[] => {
  const experiments = useExperiments()
  const { features } = useMarketplace()

  const routeExists = () => {
    if (!features.giftCards.enabled)
      return {
        message: 'Gift cards are not enabled for country',
      }

    if (experiments['experiment.giftCards'] !== 'withGiftCards')
      return {
        message: 'Gift cards experiment is not enabled',
      }

    return true
  }

  return [
    {
      path: 'checkout/gift-cards/:requestId',
      name: CHECKOUT_GIFT_CARDS.SELF,
      component: () =>
        import('~/scopes/checkout/flows/gift-card/layout/GiftCardFlow/GiftCardFlow.vue'),
      redirect: { name: CHECKOUT_GIFT_CARDS.BILLING_ADDRESS },
      meta: {
        owners: ['bot-squad-b2cservices-front'],
        // if the route exists, use no layout. If the route doesn't exist, use the default layout for the error page
        layout: routeExists() === true ? false : 'default',
        auth: {
          required: true,
          redirection: {
            name: ROUTES.AUTH.LOGIN,
            query: { bm_journey: 'checkout' },
          },
        },
        routeExists,
      },
      children: [
        {
          path: 'billing-address',
          name: CHECKOUT_GIFT_CARDS.BILLING_ADDRESS,
          component: () =>
            import('~/scopes/checkout/flows/gift-card/pages/BillingAddressStep/BillingAddressStep.vue'),
          meta: {
            owners: ['bot-squad-b2cservices-front'],
            auth: {
              required: true,
              redirection: {
                name: ROUTES.AUTH.LOGIN,
                query: { bm_journey: 'checkout' },
              },
            },
          },
        },
        {
          path: 'payment',
          name: CHECKOUT_GIFT_CARDS.PAYMENT,
          component: () =>
            import('~/scopes/checkout/flows/gift-card/pages/PaymentStep/PaymentStep.vue'),
          meta: {
            owners: ['bot-squad-b2cservices-front'],
            auth: {
              required: true,
              redirection: {
                name: ROUTES.AUTH.LOGIN,
                query: { bm_journey: 'checkout' },
              },
            },
            payment: {
              contentSecurityPoliciesEnabled: true,
            },
          },
        },
        {
          path: 'webview',
          redirect: { name: CHECKOUT_GIFT_CARDS.WEBVIEW.BILLING_ADDRESS },
          children: [
            {
              path: 'billing-address',
              name: CHECKOUT_GIFT_CARDS.WEBVIEW.BILLING_ADDRESS,
              component: () =>
                import('~/scopes/checkout/flows/gift-card/pages/BillingAddressStep/BillingAddressStep.vue'),
              meta: {
                owners: ['bot-squad-b2cservices-front'],
                auth: {
                  required: true,
                  redirection: {
                    name: ROUTES.AUTH.LOGIN,
                    query: { bm_journey: 'checkout' },
                  },
                },
              },
            },
            {
              path: 'payment',
              name: CHECKOUT_GIFT_CARDS.WEBVIEW.PAYMENT,
              component: () =>
                import('~/scopes/checkout/flows/gift-card/pages/PaymentStep/PaymentStep.vue'),
              meta: {
                owners: ['bot-squad-b2cservices-front'],
                auth: {
                  required: true,
                  redirection: {
                    name: ROUTES.AUTH.LOGIN,
                    query: { bm_journey: 'checkout' },
                  },
                },
              },
            },
          ],
        },
      ],
    },
    {
      path: 'checkout/gift-card/:requestId/payment-result',
      name: CHECKOUT_GIFT_CARDS.PAYMENT_RESULT,
      component: () =>
        import('~/scopes/checkout/pages/GiftCardsResults/GiftCardsResults.vue'),
      meta: {
        owners: ['bot-squad-b2cservices-front'],
        auth: {
          required: true,
          redirection: {
            name: ROUTES.AUTH.LOGIN,
            query: { bm_journey: 'checkout' },
          },
        },
        routeExists,
      },
    },
  ]
}
